// ImageZoomModal.jsx
import React from "react";
import { Modal, Box, IconButton } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

const ImageZoomModal = ({ open, onClose, images = [], index = 0 }) => {
  const [current, setCurrent] = React.useState(index);
  const [zoom, setZoom] = React.useState(1);
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = React.useState(false);
  const dragStart = React.useRef({ x: 0, y: 0 });

  const resetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setIsDragging(false);
  };

  const next = () => {
    setCurrent((prev) => {
      if (prev < images.length - 1) {
        resetZoom();
        return prev + 1;
      }
      return prev;
    });
  };

  const prev = () => {
    setCurrent((prev) => {
      if (prev > 0) {
        resetZoom();
        return prev - 1;
      }
      return prev;
    });
  };

  const zoomIn = () => {
    setZoom((z) => Math.min(z + 0.3, 4));
  };

  const zoomOut = () => {
    setZoom((z) => {
      const newZoom = Math.max(z - 0.3, 1);
      if (newZoom === 1) setPosition({ x: 0, y: 0 });
      return newZoom;
    });
  };

  React.useEffect(() => {
    setCurrent(index);
    resetZoom();
  }, [index, open]);

  if (!images?.length) return null;

  const handleMouseDown = (e) => {
    if (zoom <= 1) return;
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleTouchStart = (e) => {
    if (zoom <= 1) return;
    setIsDragging(true);
    dragStart.current = {
      x: e.touches[0].clientX - position.x,
      y: e.touches[0].clientY - position.y,
    };
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.touches[0].clientX - dragStart.current.x,
      y: e.touches[0].clientY - dragStart.current.y,
    });
  };

  const handleTouchEnd = () => setIsDragging(false);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",

          // ⭐ FIXED SIZE
          width: { xs: "92vw", sm: "750px" },
          height: { xs: "auto", sm: "520px" },
          maxWidth: "750px",
          maxHeight: "520px",
          aspectRatio: { xs: "750 / 520", sm: "auto" },

          bgcolor: "white",
          borderRadius: 2,
          outline: "none",
          overflow: "hidden",
        }}
      >
        {/* ⭐ IMAGE CENTERED */}
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <img
            src={images[current]}
            alt="zoom"
            draggable={false}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{
              height: "100%",
              width: "100%",
              objectFit: "contain",
              cursor: zoom > 1 ? "grab" : "default",
              transition: isDragging ? "none" : "transform 0.2s ease",
              transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
            }}
          />
        </Box>

        {/* ⭐ LEFT ARROW ALWAYS VISIBLE */}
        {images.length > 1 && current > 0 && (
          <IconButton
            onClick={prev}
            sx={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              bgcolor: "rgba(0,0,0,0.4)",
              color: "white",
              zIndex: 20,
              "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
            }}
          >
            <ArrowBackIosIcon />
          </IconButton>
        )}

        {/* ⭐ RIGHT ARROW */}
        {images.length > 1 && current < images.length - 1 && (
          <IconButton
            onClick={next}
            sx={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              bgcolor: "rgba(0,0,0,0.4)",
              color: "white",
              zIndex: 20,
              "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
            }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        )}

        {/* ⭐ ZOOM PANEL */}
        <Box
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            background: "rgba(255,255,255,0.85)",
            padding: "6px",
            borderRadius: "10px",
            zIndex: 30,
          }}
        >
          <IconButton onClick={zoomIn} disabled={zoom >= 4}>
            <AddIcon />
          </IconButton>

          <IconButton onClick={zoomOut} disabled={zoom <= 1}>
            <RemoveIcon />
          </IconButton>

          <IconButton onClick={resetZoom}>
            <RestartAltIcon />
          </IconButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default ImageZoomModal;
